import type { TFunction } from 'i18next';
import { drbfmService } from '../../../services/drbfm.service';
import type { WorkflowStartValue } from './DRBFMWorkflowStartStep';

interface ExtractionArtifactInput {
  changeReportText: string;
  oldText: string;
  newText: string;
  oldCadModelId: string;
  newCadModelId: string;
}

/**
 * Validates the input for the chosen pattern and creates the matching source
 * artifacts before the extract stage runs. Throws (with the Japanese copy the UI
 * surfaces) when a required field is missing.
 */
export const submitExtractionArtifacts = async (
  sessionId: string,
  inputPattern: WorkflowStartValue['inputPattern'],
  input: ExtractionArtifactInput,
  t: TFunction,
): Promise<void> => {
  const { changeReportText, oldText, newText, oldCadModelId, newCadModelId } = input;

  if (inputPattern === 'change_report') {
    if (!changeReportText.trim()) {
      throw new Error(t('drbfm.workflow.logic.changeReportRequired', '変化点資料を入力してください'));
    }
    await drbfmService.createWorkflowArtifact(sessionId, {
      artifactType: 'change_report',
      sourceType: 'manual_text',
      title: t('drbfm.workflow.logic.changeReportTitle', '変化点資料'),
      payload: { text: changeReportText },
    });
    return;
  }

  if (inputPattern === 'old_new_cad') {
    if (!oldCadModelId.trim() || !newCadModelId.trim()) {
      throw new Error(t('drbfm.workflow.logic.cadIdsRequired', '旧CAD IDと新CAD IDを入力してください'));
    }
    await drbfmService.createWorkflowArtifact(sessionId, {
      artifactType: 'old_cad',
      sourceType: 'cad_model',
      cadModelId: oldCadModelId.trim(),
      title: t('drbfm.workflow.logic.oldCadTitle', '旧CAD'),
      payload: {},
    });
    await drbfmService.createWorkflowArtifact(sessionId, {
      artifactType: 'new_cad',
      sourceType: 'cad_model',
      cadModelId: newCadModelId.trim(),
      title: t('drbfm.workflow.logic.newCadTitle', '新CAD'),
      payload: {},
    });
    return;
  }

  if (!oldText.trim() || !newText.trim()) {
    throw new Error(
      inputPattern === 'old_new_drawings'
        ? t('drbfm.workflow.logic.drawingsTextRequired', '旧図面と新図面の抽出テキストを入力してください')
        : t('drbfm.workflow.logic.specsRequired', '旧仕様と新仕様を入力してください'),
    );
  }
  const isDrawing = inputPattern === 'old_new_drawings';
  await drbfmService.createWorkflowArtifact(sessionId, {
    artifactType: isDrawing ? 'old_drawing' : 'old_spec',
    sourceType: 'manual_text',
    title: isDrawing
      ? t('drbfm.workflow.logic.oldDrawingTitle', '旧図面')
      : t('drbfm.workflow.logic.oldSpecTitle', '旧仕様'),
    payload: { text: oldText },
  });
  await drbfmService.createWorkflowArtifact(sessionId, {
    artifactType: isDrawing ? 'new_drawing' : 'new_spec',
    sourceType: 'manual_text',
    title: isDrawing
      ? t('drbfm.workflow.logic.newDrawingTitle', '新図面')
      : t('drbfm.workflow.logic.newSpecTitle', '新仕様'),
    payload: { text: newText },
  });
};
